// Ô "Của ai" ở danh sách đơn của chế độ Quản lý (Đại lý, CTV): tất cả · của tôi · cấp dưới · từng đơn vị.
// Giá trị giữ trên URL (?unit=) để bảng "Đơn theo người bán" ở Tổng quan mở thẳng đúng người bán.
// Danh sách đơn vị: GET /orders/network-units (đúng phạm vi GET /orders?scope=all).

import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { get } from '@/lib/api'

export interface NetworkUnit {
  id: string
  name: string
  tenantType: string
  isOwn: boolean
  parentName: string | null
}

/** 'all' | 'own' | 'downline' | mã đơn vị */
export type UnitChoice = string

const TYPE_LABEL: Record<string, string> = { agency: 'Đại lý', ctv: 'CTV', shop: 'Shop' }

export function useNetworkUnits() {
  return useQuery({
    queryKey: ['orders-network-units'],
    queryFn: () => get<{ units: NetworkUnit[] }>('/orders/network-units').then((r) => r.units),
    staleTime: 60_000,
  })
}

/** Lựa chọn hiện tại trên URL (?unit=). Mã của chính mình quy về 'own'. */
export function useUnitChoice(selfShopId: string | null): [UnitChoice, (v: UnitChoice) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get('unit') || 'all'
  const value = raw === selfShopId ? 'own' : raw
  const set = (v: UnitChoice) => {
    const next = new URLSearchParams(params)
    if (v === 'all') next.delete('unit')
    else next.set('unit', v)
    setParams(next, { replace: true })
  }
  return [value, set]
}

/** Thêm scope / unit vào truy vấn GET /orders theo lựa chọn. */
export function applyUnitChoice(params: URLSearchParams, choice: UnitChoice, selfShopId: string | null): URLSearchParams {
  if (choice === 'downline') {
    params.set('scope', 'network')
    return params
  }
  params.set('scope', 'all')
  if (choice === 'own' && selfShopId) params.set('unit', selfShopId)
  else if (choice !== 'all' && choice !== 'own') params.set('unit', choice)
  return params
}

export function UnitFilter({ value, onChange }: { value: UnitChoice; onChange: (v: UnitChoice) => void }) {
  const { data: units } = useNetworkUnits()
  const others = (units ?? []).filter((u) => !u.isOwn).sort((a, b) => a.name.localeCompare(b.name, 'vi'))
  const known = value === 'all' || value === 'own' || value === 'downline' || others.some((u) => u.id === value)
  return (
    <select
      value={known || !units ? value : 'all'}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Lọc theo thành viên"
      className="h-11 w-full rounded-lg border border-rule bg-raise px-3 text-sm sm:w-64 desktop:h-10"
    >
      <option value="all">Tất cả thành viên</option>
      <option value="own">Của tôi</option>
      <option value="downline">Cấp dưới</option>
      {others.length > 0 && (
        <optgroup label="Từng đơn vị">
          {others.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} · {TYPE_LABEL[u.tenantType] ?? u.tenantType}
              {u.parentName ? ` (dưới ${u.parentName})` : ''}
            </option>
          ))}
        </optgroup>
      )}
      {/* Mã từ URL chưa có trong danh sách (đang tải) — giữ để không nhảy về "Tất cả" */}
      {!known && !units && <option value={value}>Đang tải…</option>}
    </select>
  )
}
